/**
 * Contabilidade de filtros de uma grid: quantos estao ligados, como limpar
 * todos de uma vez, e como voltar a primeira pagina ao mudar o recorte.
 *
 * Traduz FR-005 (mudar filtro reposiciona na primeira pagina) e FR-012 (limpar
 * tudo numa acao, e ver que ha filtro ativo sem inspecionar cada controle).
 *
 * EXISTE PORQUE O MESMO TRABALHO APARECIA DE QUATRO FORMAS EM 16 TELAS, e as
 * divergencias nao eram cosmeticas:
 *
 *   - `filtrar()` — o wrapper que zera a pagina ao mexer num filtro — estava
 *     duplicado IDENTICO em 11 arquivos.
 *   - "algum filtro ligado?" tinha quatro respostas: uma lista declarativa
 *     (`convenios`), um booleano ad-hoc (8 telas), uma expressao inline dentro
 *     do proprio `emptyState` sem nome nenhum (2 telas), e nada (5 telas).
 *   - `averbadora/auditoria.tsx` tem SEIS filtros, incluindo um intervalo de
 *     data/hora, e nenhum reset — nao ha o que clicar para voltar ao normal.
 *   - em `bancos`/`prefeituras`/`perfis`, a checagem "tem filtro?" e o reset
 *     sao duas listas mantidas a mao a 80-160 linhas de distancia. E' o risco
 *     que o comentario de `convenios.tsx` descreve: uma dimensao nova
 *     esquecida num dos dois vira filtro que nao conta, ou que "Limpar" nao
 *     limpa — e o operador so' descobre quando a grid nao volta ao normal.
 *
 * ---
 *
 * `ligado` E' DECLARADO, NAO INFERIDO. Nao e' cerimonia: um hook que decidisse
 * sozinho com `Boolean(valor)` erraria em quatro telas reais deste repo.
 *
 *   - `trava-margem-monitor` filtra por id de unidade, onde o valor neutro e'
 *     `null` e o id `0` e' valido. `!valor` perderia a unidade 0 em silencio —
 *     e por isso a tela ja testa `fUnidade != null` a mao.
 *   - `adf` (`status: "recebida"`), `banco/ofertas` (`aba: "ativas"`) e
 *     `motor-rubricas` (`soConsignacao: true`) tem default NAO-VAZIO. Para
 *     elas "ligado" e' `valor !== default`, e `limpar` e' RESTAURAR o default,
 *     nao esvaziar. Um default tratado como "sem filtro" faria o reset abrir a
 *     fila inteira de ADFs quando o operador queria so' voltar as Recebidas.
 *   - `folhas` restaura por outra via ainda: esvazia `compFim` e deixa um
 *     efeito re-derivar as ultimas 12 competencias das facetas. `limpar` ser
 *     uma funcao — e nao um valor que o hook atribui — e' o que permite isso.
 *
 * Declarar custa uma linha por dimensao e e' a unica forma de as quatro
 * estarem certas ao mesmo tempo.
 *
 * SEM MEMO, de proposito: o hook nao guarda estado nem faz efeito, e a lista de
 * dimensoes e' recriada a cada render (as `limpar` sao arrow functions). Um
 * `useMemo` sobre ela nunca acertaria a cache e daria a impressao falsa de que
 * `limparFiltros` tem identidade estavel. Ele e' usado em `onClick`, nunca como
 * dependencia de efeito.
 */

/** Uma dimensao de recorte da grid. */
export interface DimensaoFiltro {
  /**
   * A dimensao esta cortando o resultado agora?
   *
   * Declarado pela tela, nunca inferido pelo hook — ver o cabecalho. Numa
   * dimensao com default nao-vazio isto e' `valor !== default`; numa de id e'
   * `id != null`; numa de multi-selecao e' `sel.length > 0`.
   */
  ligado: boolean;
  /**
   * Devolve a dimensao ao neutro.
   *
   * Pode ser esvaziar (`setBusca("")`), RESTAURAR um default nao-vazio
   * (`setStatus("recebida")`) ou esvaziar para que um efeito re-derive o
   * default (o caso das 12 competencias de `folhas`).
   */
  limpar: () => void;
  /**
   * Pre-condicao da tela, e nao filtro: a prefeitura antes de
   * `servidores/visualizar`, a folha antes do Motor de rubricas.
   *
   * Nao entra no contador e o reset nao mexe. Sem ela a tela nao tem dado
   * nenhum para mostrar, entao "limpar" esvaziaria a tela em vez de ampliar o
   * recorte — o oposto do que o operador pediu ao clicar. As duas telas que
   * tem contexto hoje acertam isso a mao; declarar transforma o acerto em
   * regra.
   */
  contexto?: boolean;
  /** Rotulo curto da dimensao, para diagnostico e para o resumo de "Mais filtros". */
  nome?: string;
}

export interface Filtros {
  /** Quantas dimensoes de RECORTE estao ligadas. Contexto nao conta. */
  filtrosAtivos: number;
  temFiltro: boolean;
  /** Limpa todas as dimensoes de recorte e volta a primeira pagina. */
  limparFiltros: () => void;
  /** Envolve um setter para que mexer no filtro volte a primeira pagina (FR-005). */
  filtrar: <T>(set: (v: T) => void) => (v: T) => void;
  /** Pronto para espalhar no `FilterBar`: `{...filtros.propsFilterBar}`. */
  propsFilterBar: {
    onReset: () => void;
    resetActive: boolean;
    resetLabel: string | undefined;
  };
}

export function useFiltros(
  dimensoes: readonly DimensaoFiltro[],
  setPagina: (p: number) => void,
): Filtros {
  const recortaveis = dimensoes.filter((d) => !d.contexto);
  const filtrosAtivos = recortaveis.filter((d) => d.ligado).length;
  const temFiltro = filtrosAtivos > 0;

  const limparFiltros = () => {
    for (const d of recortaveis) d.limpar();
    setPagina(1);
  };

  const filtrar = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPagina(1);
  };

  return {
    filtrosAtivos,
    temFiltro,
    limparFiltros,
    filtrar,
    propsFilterBar: {
      onReset: limparFiltros,
      resetActive: temFiltro,
      // `undefined` deixa o `FilterBar` usar o rotulo padrao — o botao continua
      // SEMPRE visivel, e o que muda com filtro aplicado e' a enfase mais a
      // contagem. Texto e formato sao os mesmos que `convenios.tsx` ja
      // mostrava; se o rotulo mudar, a extracao mudou comportamento.
      resetLabel: temFiltro ? `✕ Limpar filtros (${filtrosAtivos})` : undefined,
    },
  };
}
